import { IconFilterOff } from '@tabler/icons-react'
import { parseISO } from 'date-fns'
import { lowerCase, sortBy } from 'lodash'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'

import { CorePageMessage } from './CorePageMessage'

import { FilterObjectType, useFilters } from '@/context/filterContext'
import { useListViewInfo } from '@/context/listViewContext'
import { useNavigation } from '@/context/navigationContext'
import { useProject } from '@/context/projectContext'
import { useQueryParams } from '@/context/queryParamsContext'
import {
  FormateDateRangeStringType,
  useTimezonedDates,
} from '@/context/timezoneContext'
import { Params } from '@/utils/params'
import { pluralise, pluraliseWord } from '@/utils/pluralise'

import { QuerySupport } from 'types/navigation'

const BLACKLISTED_FILTER_KEYS = [
  'tab',
  'direction' as keyof Params.Sort,
  'sortKey' as keyof Params.Sort,
  'pageSize' as keyof Params.Pagination,
]

const Filter = styled.span`
  display: grid;
  grid-template-columns: 1fr 14px 1fr;
  gap: 0.5rem;

  & > *:first-child {
    text-align: right;
  }

  & > *:last-child {
    text-align: left;
  }
`

const Capital = styled.span`
  text-transform: capitalize;
`

type Filter = FilterObjectType['filters']

type HumanReadableFilter = {
  key: string
  value: string
}

type OptimisticObject = {
  name?: string
  title?: string
  text?: string
  label?: string
  afterDateTime?: string
  beforeDateTime?: string
}

type Formatters = {
  date: FormateDateRangeStringType
  dateRange: FormateDateRangeStringType
}

const format = (value: string, formatters: Formatters) => {
  const dateValue = parseISO(value)

  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    return formatters.date({ date: value })
  }

  return lowerCase(value)
}

const isLikelyACuid = (value: string) => {
  return (
    value.startsWith('c') &&
    (value.length === 25 || value.length === 26) &&
    /^[A-Za-z0-9]+$/g.test(value)
  )
}

const getOptimisticObjectValue = (
  value: OptimisticObject,
  formatters: Formatters
) => {
  if (!value) return null

  const { name, title, text, label } = value
  const humanReadableValue = name || title || text || label

  if (humanReadableValue) {
    return format(humanReadableValue, formatters)
  }

  if (value.afterDateTime && value.beforeDateTime) {
    return formatters.dateRange({
      date: [value.afterDateTime, value.beforeDateTime],
      includeTime: false,
    })
  }

  return null
}

const humanReadableFilter = (
  key: string,
  value: Filter[number],
  formatters: Formatters
): HumanReadableFilter => {
  if (BLACKLISTED_FILTER_KEYS.includes(key)) return null

  const formattedKey = format(key, formatters)

  if (Array.isArray(value)) {
    if (value.length === 0) return null

    if (typeof value[0] === 'string') {
      if (isLikelyACuid(value[0])) {
        return {
          key: formattedKey,
          value: pluralise(value.length, formattedKey),
        }
      }

      const stringValues = (value as string[]).filter((v) => !!v.trim())

      if (stringValues.length === 0) return null

      return {
        key: formattedKey,
        value: stringValues.map((s) => format(s, formatters)).join(', '),
      }
    }

    if (typeof value[0] === 'object') {
      const objectValues = (value as OptimisticObject[])
        .map((o) => getOptimisticObjectValue(o, formatters))
        .filter((o) => !!o)

      if (objectValues.length === 0) return null

      return {
        key: formattedKey,
        value: objectValues.join(', '),
      }
    }

    if (typeof value[0] === 'boolean' && value.length === 1) {
      return {
        key: formattedKey,
        value: value[0] ? 'True' : 'False',
      }
    }
  }

  if (typeof value === 'object') {
    const objectValue = getOptimisticObjectValue(
      value as OptimisticObject,
      formatters
    )

    if (!objectValue) return null

    return {
      key: formattedKey,
      value: objectValue,
    }
  }

  if (typeof value === 'boolean') {
    return {
      key: formattedKey,
      value: value ? 'Yes' : 'No',
    }
  }

  if (typeof value === 'string') {
    if (isLikelyACuid(value)) {
      return {
        key: formattedKey,
        value: `1 ${formattedKey}`,
      }
    }

    return {
      key: formattedKey,
      value: format(value, formatters),
    }
  }

  return null
}

export const tryGetHumanReadableFilters = (
  filters: Filter,
  formatters: Formatters
) => {
  if (!filters) return []

  return Object.keys(filters)
    .map((key) => humanReadableFilter(key, filters[key], formatters))
    .filter((f) => f !== null)
}

type Props = {
  useQuery?: boolean
}

export const CorePageListViewEmpty: React.FC<Props> = ({ useQuery }) => {
  const { project } = useProject()
  const { currentNavItem } = useNavigation()
  const { resourceSingular } = useListViewInfo()
  const { routeFilters, clearFilters } = useFilters()
  const { params, clearParams } = useQueryParams()

  const { formatDateString, formatDateRangeString } = useTimezonedDates()

  const filters = useQuery ? params : routeFilters.filters
  const filterKeys = Object.keys(filters ?? {})

  const showProject =
    currentNavItem?.querySupport !== QuerySupport.BUILDER_ONLY && !!project

  if (filterKeys.length === 0) {
    return (
      <CorePageMessage
        message={`No ${pluraliseWord(2, resourceSingular)} found
        ${showProject ? `for ${project.name}` : ''}`}
      />
    )
  }

  const humanReadableFilters: HumanReadableFilter[] = [
    ...tryGetHumanReadableFilters(filters, {
      date: formatDateString,
      dateRange: formatDateRangeString,
    }),
    ...(showProject ? [{ key: 'Project', value: project.name }] : []),
  ]

  const sortedFilters = sortBy(humanReadableFilters, (f) => f.value.length)

  const clearAll = () => {
    useQuery ? clearParams() : clearFilters()
  }

  return (
    <CorePageMessage
      message={`Cannot find any ${pluraliseWord(
        2,
        resourceSingular
      )} with the applied
        filters:`}
    >
      {sortedFilters.reverse().map(({ key, value }) => (
        <Filter key={key}>
          <Capital as="strong">{key}</Capital>
          <span>→</span>
          <Capital>{value}</Capital>
        </Filter>
      ))}
      <br />
      <Button
        variant="secondary"
        icon={<IconFilterOff size={16} />}
        onClick={clearAll}
        style={{ alignSelf: 'center' }}
      >
        Clear filters
      </Button>
    </CorePageMessage>
  )
}
